import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical, Eye, Plus, Edit, ListChecks, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDropdown } from '@/hooks/useDropdown';
import type { Job } from '@maker-toolchest/shared';

interface JobActionsMenuProps {
  job: Job;
  onAddTasks: () => void;
  onUpdateStatus: () => void;
  onViewTasks: () => void;
  onCancelJob: () => void;
}

export function JobActionsMenu({
  job,
  onAddTasks,
  onUpdateStatus,
  onViewTasks,
  onCancelJob,
}: JobActionsMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const navigate = useNavigate();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const menuRef = useDropdown({
    isOpen,
    onClose: () => setIsOpen(false),
  });

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right + window.scrollX - 192, // 192px = w-48 (12rem)
      });
    }
  }, [isOpen]);

  const handleViewDetails = () => {
    setIsOpen(false);
    navigate(`/projects/jobs/${job.id}`);
  };

  const handleAddTasks = () => {
    setIsOpen(false);
    onAddTasks();
  };

  const handleUpdateStatus = () => {
    setIsOpen(false);
    onUpdateStatus();
  };

  const handleViewTasks = () => {
    setIsOpen(false);
    onViewTasks();
  };

  const handleCancelJob = () => {
    setIsOpen(false);
    onCancelJob();
  };

  return (
    <>
      <div onClick={(e) => e.stopPropagation()}>
        {/* Trigger button */}
        <button
          ref={buttonRef}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          aria-expanded={isOpen}
          aria-haspopup="true"
          aria-label="Job actions"
          className="
            flex h-8 w-8 items-center justify-center rounded-md
            text-foreground-muted hover:bg-background-hover hover:text-foreground
            transition-colors
          "
        >
          <MoreVertical className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {/* Dropdown menu - rendered in portal */}
      {isOpen && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={{
            position: 'fixed',
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
          }}
          className="
            w-48 rounded-md border border-border
            bg-background-elevated shadow-lg z-50
          "
        >
          <div className="py-1">
            <button
              onClick={handleViewDetails}
              role="menuitem"
              className="
                flex w-full items-center gap-2 px-4 py-2 text-sm
                text-foreground-muted hover:bg-background-hover hover:text-foreground
              "
            >
              <Eye className="h-4 w-4" aria-hidden="true" />
              View Details
            </button>
            <button
              onClick={handleAddTasks}
              role="menuitem"
              className="
                flex w-full items-center gap-2 px-4 py-2 text-sm
                text-foreground-muted hover:bg-background-hover hover:text-foreground
              "
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Add Tasks
            </button>
            <button
              onClick={handleUpdateStatus}
              role="menuitem"
              className="
                flex w-full items-center gap-2 px-4 py-2 text-sm
                text-foreground-muted hover:bg-background-hover hover:text-foreground
              "
            >
              <Edit className="h-4 w-4" aria-hidden="true" />
              Update Status
            </button>
            <button
              onClick={handleViewTasks}
              role="menuitem"
              className="
                flex w-full items-center gap-2 px-4 py-2 text-sm
                text-foreground-muted hover:bg-background-hover hover:text-foreground
              "
            >
              <ListChecks className="h-4 w-4" aria-hidden="true" />
              Update Task
            </button>
            <button
              onClick={handleCancelJob}
              role="menuitem"
              className="
                flex w-full items-center gap-2 px-4 py-2 text-sm
                text-foreground-muted hover:bg-background-hover hover:text-foreground
              "
            >
              <XCircle className="h-4 w-4" aria-hidden="true" />
              Cancel Job
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
